/**
 * Converts TypedValue JSON (from GraphQL) into React nodes using the shared
 * TagValueTypedValueViewer — the same leaf renderer used by structured tables.
 */
import {Stack, Text} from '@mantine/core';
import React from 'react';

import TagValueTypedValueViewer from 'notebook/tables/typed-values/components/viewers/TagValueTypedValueViewer';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {ProcedureParameterDataTypeDefinition} from 'procedures/ProcedureDomainTypes';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {SchemaFieldRenderData} from 'schemas/fields/SchemaFieldConstants';
import {isItemLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureMethodExecutionInstanceChip from 'ui-library/chips/ProcedureMethodExecutionInstanceChip';

import MultiValueDisplay from './MultiValueDisplay';
import type {LinkedPMEI} from './PMEIMaterialLinkUtils';

function buildSchemaFieldRenderData(dataType: ProcedureParameterDataTypeDefinition): SchemaFieldRenderData {
  const unitSymbol = dataType.unit != null ? dataType.unit.symbol : null;
  return {name: '', fieldType: dataType.type, unitSymbol};
}

function isEntityLinkField(dataType: ProcedureParameterDataTypeDefinition): boolean {
  return isItemLinkFieldType(dataType.type);
}

function renderTypedValue(
  typedValue: TypedValue,
  textValue: string,
  schemaFieldRenderData: SchemaFieldRenderData,
  showDateChips: boolean
): React.ReactNode {
  return (
    <TagValueTypedValueViewer
      chipSize={ChipSize.TABLE_CELL}
      shouldPreviewImages={false}
      showChipsAsLinks={true}
      summarize={false}
      summarizeThreshold={10}
      typedValue={typedValue}
      textValue={textValue}
      schemaFieldRenderData={schemaFieldRenderData}
      shouldShowDateAndDatetimesAsChips={showDateChips}
    />
  );
}

function renderEntityValues(
  values: Array<TypedValue>,
  schemaFieldRenderData: SchemaFieldRenderData
): React.ReactNode | null {
  if (values.length === 0) {
    return null;
  }
  const rendered = values.map((v) => renderTypedValue(v, '', schemaFieldRenderData, false));
  return <MultiValueDisplay values={rendered} />;
}

function renderParameterValue(
  typedValue: TypedValue,
  schemaFieldRenderData: SchemaFieldRenderData
): React.ReactNode | null {
  if (typedValue == null) {
    return null;
  }

  const textValue = formatTypedValue(typedValue);

  if (!textValue) {
    return null;
  }

  return renderTypedValue(typedValue, textValue, schemaFieldRenderData, true);
}

function formatMaterialInputAmount(
  amount: TypedValue | null | undefined,
  amountUnit: UnitSummary | null
): string | null {
  if (amount == null) {
    return null;
  }
  const amountText = formatTypedValue(amount);
  if (!amountText) {
    return null;
  }
  const unitSymbol = amountUnit?.symbol;
  return unitSymbol != null && unitSymbol !== '' ? `${amountText} ${unitSymbol}` : amountText;
}

function getMaterialInputParts(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): {materialNode: React.ReactNode; amountText: string | null} | null {
  const materialText = formatTypedValue(material);
  if (!materialText) {
    return null;
  }
  const materialNode = renderTypedValue(material, materialText, schemaFieldRenderData, false);
  const amountText = formatMaterialInputAmount(amount, amountUnit);
  return {materialNode, amountText};
}

/** Cell display: material stacked above its amount, left-aligned. */
function renderMaterialInputValueStacked(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): React.ReactNode | null {
  const parts = getMaterialInputParts(material, amount, schemaFieldRenderData, amountUnit);
  if (parts == null) {
    return null;
  }
  if (parts.amountText == null) {
    return parts.materialNode;
  }
  return (
    <Stack spacing={2} align="flex-start">
      {parts.materialNode}
      <Text fz="xs" c="dimmed">
        ({parts.amountText})
      </Text>
    </Stack>
  );
}

/** Tooltip display: material and amount inline as "material (amount)" so a long readings list stays compact. */
function renderMaterialInputValueInline(
  material: TypedValue,
  amount: TypedValue | null | undefined,
  schemaFieldRenderData: SchemaFieldRenderData,
  amountUnit: UnitSummary | null
): React.ReactNode | null {
  const parts = getMaterialInputParts(material, amount, schemaFieldRenderData, amountUnit);
  if (parts == null) {
    return null;
  }
  if (parts.amountText == null) {
    return parts.materialNode;
  }
  return (
    <>
      {parts.materialNode}{' '}
      <Text span={true} inherit={true} c="dimmed">
        ({parts.amountText})
      </Text>
    </>
  );
}

export function buildMaterialInputCell(
  values: Array<{material: TypedValue; amount: TypedValue | null | undefined}>,
  dataType: ProcedureParameterDataTypeDefinition,
  amountUnit: UnitSummary | null
): React.ReactNode {
  const schema = buildSchemaFieldRenderData(dataType);
  const rendered = values
    .map(({material, amount}) => renderMaterialInputValueStacked(material, amount, schema, amountUnit))
    .filter((v): v is React.ReactNode => v != null);
  const tooltipRendered = values
    .map(({material, amount}) => renderMaterialInputValueInline(material, amount, schema, amountUnit))
    .filter((v): v is React.ReactNode => v != null);
  return <MultiValueDisplay values={rendered} tooltipValues={tooltipRendered} />;
}

export function buildCell(
  values: Array<TypedValue>,
  dataType: ProcedureParameterDataTypeDefinition
): React.ReactNode {
  const filteredValues = values.filter((v) => v != null);
  if (filteredValues.length === 0) {
    return null;
  }
  const schema = buildSchemaFieldRenderData(dataType);
  if (isEntityLinkField(dataType)) {
    return renderEntityValues(filteredValues, schema);
  }
  const rendered = filteredValues
    .map((v) => renderParameterValue(v, schema))
    // This filter for null is needed because renderParameterValue can return null.
    .filter((v): v is React.ReactNode => v != null);
  return <MultiValueDisplay values={rendered} />;
}

function renderLinkCell(
  pmeis: Array<LinkedPMEI>,
  onNavigateToPmei: ((pmeiId: string) => void) | null
): React.ReactNode {
  if (pmeis.length === 0) {
    return (
      <Text fz="sm" c="dimmed" fs="italic">
        Pending
      </Text>
    );
  }
  const chips = pmeis.map((pmei) => (
    <ProcedureMethodExecutionInstanceChip
      key={pmei.id}
      id={pmei.id}
      size={ChipSize.SMALL}
      displayNameOverride={`${pmei.methodName} · ${pmei.conditionName}-${pmei.replicateNumber}`}
      {...(onNavigateToPmei != null
        ? {
            onClick: () => {
              onNavigateToPmei(pmei.id);
            },
          }
        : {})}
    />
  ));
  return <MultiValueDisplay values={chips} />;
}

export function renderMaterialLinkCell(
  versionId: string,
  index: Map<string, Array<LinkedPMEI>> | null,
  noEdgeLabel: string,
  onNavigateToPmei: ((pmeiId: string) => void) | null
): React.ReactNode {
  if (index == null) {
    return null;
  }
  const linkedPMEIs = index.get(versionId);
  if (linkedPMEIs == null) {
    return (
      <Text fz="sm" c="dimmed">
        {noEdgeLabel}
      </Text>
    );
  }
  return renderLinkCell(linkedPMEIs, onNavigateToPmei);
}
